import type { FC } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { parseFloatOrFallback } from '@wix/da-shared-react/pkg/EarningsCalculator';
import StripeWithdrawSetupModal from '../StripeWithdrawSetupModal';
import StripeWithdrawIncompleteConnectionModal from '../StripeWithdrawIncompleteConnectionModal';
import StripeWithdrawAmountModal from '../StripeWithdrawAmountModal';
import StripeWithdrawConfirmationModal from '../StripeWithdrawConfirmationModal';
import {
  getAvailableBalance,
  getStripeStatus,
  getStripeMinimumWithdrawalAmount,
} from '../../../../../selectors/earnings';

export interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'setup' | 'incomplete' | 'amount' | 'confirmation';

const StripeWithdrawRootModal: FC<Props> = ({ isOpen, onClose }) => {
  const availableBalance = useSelector(getAvailableBalance);
  const stripeStatus = useSelector(getStripeStatus);
  const stripeMinimumWithdrawalAmount = useSelector(
    getStripeMinimumWithdrawalAmount
  );

  // Determine initial step based on connection status
  const getInitialStep = useCallback((): ModalStep => {
    if (!stripeStatus) {
      return 'setup';
    }
    if (stripeStatus === 'incomplete_connection') {
      return 'incomplete';
    }
    if (stripeStatus === 'connected') {
      return 'amount';
    }
    return 'setup';
  }, [stripeStatus]);

  const [currentStep, setCurrentStep] = useState<ModalStep>('setup');

  // Reset to initial step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(getInitialStep());
    }
  }, [isOpen, getInitialStep]);

  const handleSetupContinue = useCallback(() => {
    setCurrentStep('amount');
  }, []);

  const handleIncompleteContinue = useCallback(() => {
    // After completing onboarding, user will be redirected back
    // The page will reload and status should be 'connected'
    onClose();
    window.location.reload();
  }, [onClose]);

  const handleAmountContinue = useCallback(() => {
    setCurrentStep('confirmation');
  }, []);

  const handleConfirmationDone = useCallback(() => {
    onClose();
    window.location.reload();
  }, [onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseAndReload = useCallback(() => {
    handleClose();
    window.location.reload();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <StripeWithdrawSetupModal
        isOpen={currentStep === 'setup'}
        requestClose={handleClose}
        onSuccess={handleSetupContinue}
      />
      <StripeWithdrawIncompleteConnectionModal
        isOpen={currentStep === 'incomplete'}
        requestClose={handleClose}
        onSuccess={handleIncompleteContinue}
      />
      <StripeWithdrawAmountModal
        isOpen={currentStep === 'amount'}
        requestClose={handleClose}
        availableBalance={parseFloatOrFallback(availableBalance, 0)}
        minimumWithdrawalAmount={parseFloatOrFallback(
          stripeMinimumWithdrawalAmount,
          0
        )}
        onContinue={handleAmountContinue}
      />
      <StripeWithdrawConfirmationModal
        isOpen={currentStep === 'confirmation'}
        requestClose={handleCloseAndReload}
        onDone={handleConfirmationDone}
      />
    </>
  );
};

StripeWithdrawRootModal.displayName = 'StripeWithdrawRootModal';

export default StripeWithdrawRootModal;
